import { ZeroAddress } from 'ethers'

export type Config = {
    contractAddresses: {
        xUnity: string
        xUnityTrinity: string
        unity: string
        mUnity: string
        mUnityTrinity: string
        management: string
        depositReceipt: string
        redemptionReceipt: string
    }
    buyLinks: {
        AVAX: string
    }
    pharaoh: {
        contracts: {
            V3_FACTORY: string
            V3_SWAP_ROUTER: string
            QUOTER_V1: string
            QUOTER_V2: string
            POSITION_MANAGER: string
        }
        tokens: {
            AVAX: string
            WAVAX: string
            UTY: string
            USDT: string
        }
        fees: {
            DEFAULT_BASIS_POINTS: number
            DEFAULT_PERCENTAGE: number
        }
    }
    chainId: number
}

export const Environments = {
    Local: 'local',
    Development: 'development',
    Production: 'production',
} as const

export type Environment = (typeof Environments)[keyof typeof Environments]

type ConfigByEnvironment = Record<Environment, Config>

export function getEnvironment(): Environment {
    const hostname = window.location.hostname
    if (hostname === 'localhost') {
        return Environments.Local
    }
    if (hostname.startsWith('dev')) {
        return Environments.Development
    }
    return Environments.Production
}

export const getConfig = (environmentName: Environment, env: Record<string, string> = import.meta.env): Config => {
    const configMap: ConfigByEnvironment = {
        [Environments.Local]: {
            contractAddresses: {
                xUnity: env.VITE_XUNITY_CONTRACT_ADDRESS,
                xUnityTrinity: env.VITE_XUNITY_TRINITY_CONTRACT_ADDRESS,
                unity: env.VITE_UNITY_CONTRACT_ADDRESS,
                mUnity: env.VITE_MUNITY_CONTRACT_ADDRESS,
                mUnityTrinity: env.VITE_MUNITY_TRINITY_CONTRACT_ADDRESS,
                management: env.VITE_MANAGEMENT_CONTRACT_ADDRESS,
                depositReceipt: env.VITE_DEPOSIT_RECEIPT_CONTRACT_ADDRESS,
                redemptionReceipt: env.VITE_REDEMPTION_RECEIPT_CONTRACT_ADDRESS,
            },
            buyLinks: {
                AVAX: env.VITE_BUY_AVAX_LINK || 'https://app.finance/',
            },
            pharaoh: {
                contracts: {
                    V3_FACTORY: env.VITE_PHARAOH_V3_FACTORY || ZeroAddress,
                    V3_SWAP_ROUTER: env.VITE_PHARAOH_V3_SWAP_ROUTER || ZeroAddress,
                    QUOTER_V1: env.VITE_PHARAOH_QUOTER_V1 || ZeroAddress,
                    QUOTER_V2: env.VITE_PHARAOH_QUOTER_V2 || ZeroAddress,
                    POSITION_MANAGER: env.VITE_PHARAOH_POSITION_MANAGER || ZeroAddress,
                },
                tokens: {
                    AVAX: env.VITE_AVAX_TOKEN_ADDRESS || ZeroAddress,
                    WAVAX: env.VITE_WAVAX_TOKEN_ADDRESS || ZeroAddress,
                    UTY: env.VITE_UTY_TOKEN_ADDRESS || ZeroAddress,
                    USDT: env.VITE_USDT_TOKEN_ADDRESS || ZeroAddress,
                },
                fees: {
                    DEFAULT_BASIS_POINTS: Number.parseInt(env.VITE_DEFAULT_FEE_BASIS_POINTS || '250'),
                    DEFAULT_PERCENTAGE: Number.parseInt(env.VITE_DEFAULT_FEE_BASIS_POINTS || '250') / 10000,
                },
            },
            chainId: Number.parseInt(env.VITE_CHAIN_ID || '43114'),
        },
        [Environments.Development]: {
            contractAddresses: {
                xUnity: ZeroAddress,
                xUnityTrinity: ZeroAddress,
                unity: ZeroAddress,
                mUnity: ZeroAddress,
                mUnityTrinity: ZeroAddress,
                management: ZeroAddress,
                depositReceipt: ZeroAddress,
                redemptionReceipt: ZeroAddress,
            },
            buyLinks: {
                AVAX: 'https://app.finance/',
            },
            pharaoh: {
                contracts: {
                    V3_FACTORY: ZeroAddress,
                    V3_SWAP_ROUTER: ZeroAddress,
                    QUOTER_V1: ZeroAddress,
                    QUOTER_V2: ZeroAddress,
                    POSITION_MANAGER: ZeroAddress,
                },
                tokens: {
                    AVAX: ZeroAddress,
                    WAVAX: ZeroAddress,
                    UTY: ZeroAddress,
                    USDT: ZeroAddress,
                },
                fees: {
                    DEFAULT_BASIS_POINTS: 250,
                    DEFAULT_PERCENTAGE: 250 / 10000,
                },
            },
            chainId: 43113,
        },
        [Environments.Production]: {
            contractAddresses: {
                xUnity: ZeroAddress,
                xUnityTrinity: ZeroAddress,
                unity: '0xDBc5192A6B6FfEe7451301bb4ec312f844F02B4A',
                mUnity: ZeroAddress,
                mUnityTrinity: ZeroAddress,
                management: '0x9B9cF4f6255f6b451132Bdf7a3682c7299D4c77A',
                depositReceipt: '0x65A83C6A678f7849D3d3AeAB6bbaBe9AD1D33B6e',
                redemptionReceipt: '0x3183236cDCE208888c1857c2F718e3628c063B73',
            },
            buyLinks: {
                AVAX: 'https://app.finance/',
            },
            pharaoh: {
                contracts: {
                    V3_FACTORY: ZeroAddress,
                    V3_SWAP_ROUTER: ZeroAddress,
                    QUOTER_V1: ZeroAddress,
                    QUOTER_V2: ZeroAddress,
                    POSITION_MANAGER: ZeroAddress,
                },
                tokens: {
                    AVAX: ZeroAddress,
                    WAVAX: ZeroAddress,
                    UTY: ZeroAddress,
                    USDT: ZeroAddress,
                },
                fees: {
                    DEFAULT_BASIS_POINTS: 250,
                    DEFAULT_PERCENTAGE: 250 / 10000,
                },
            },
            chainId: 43114,
        },
    }
    return configMap[environmentName]
}
